import { jsx as _jsx } from "react/jsx-runtime";
import { useThree } from '@react-three/fiber';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { DoubleSide, FrontSide, SRGBColorSpace, TextureLoader } from 'three';
import { IFontTypes, ITextAlignment } from '../component-data-structure';
import { convertRgbaToHex } from '../utils/general';
import { getGradientCanvas, getSvgCanvas } from './CurvedEntity/canvasTexture';
import CurvedEntity from './CurvedEntity/CurvedEntity';
const Button = ({ scale: s, color: c, text, borderWidth: bw = 0, borderRgba: bCol = [255, 255, 255, 1], fontFamily = IFontTypes.Roboto400, borderRadius: radius = 0, fontSize = 2, fontRgba = [0, 0, 0, 1], position, rotation, textAlignment = ITextAlignment.center, svgUrl = undefined, onPointerUp, onPointerMove, onPointerDown, onDoubleClick, renderOrder = 0, curvature, id = '', raycast, nonTransientScale, castShadow = false, receiveShadow = false, animationTransformGroupPrefix = '', onTextSync, isTextScaleLocked = false, side }) => {
    // memoize reference type props
    const zeroPos = useMemo(() => [0, 0, 0], []);
    const brgba = useMemo(() => bCol, [bCol]);
    const scale = useMemo(() => s, [s]);
    const userData = useMemo(() => ({ renderOrder, contentId: id }), [id, renderOrder]);
    const color = useMemo(() => c, [c]);
    const disableDoubleClick = svgUrl !== undefined;
    const [texture, setTexture] = useState();
    const { gl } = useThree();
    useEffect(() => {
        const getData = async () => {
            if (!nonTransientScale)
                return;
            const image = await (svgUrl
                ? getSvgCanvas(svgUrl)
                : getGradientCanvas({
                    colors: convertRgbaToHex(color),
                    width: Math.abs(nonTransientScale[0]) * 100,
                    height: Math.abs(nonTransientScale[1]) * 100,
                }));
            const imageTexture = await new TextureLoader().loadAsync(image);
            setTexture(imageTexture);
        };
        // If only 1 color, use MeshBasicMaterial with color instead of map, so don't need a texture
        if (color.length === 1 && !svgUrl) {
            setTexture(undefined);
        }
        else {
            getData();
        }
    }, [svgUrl, color, nonTransientScale, gl]);
    if (texture) {
        texture.anisotropy = gl.capabilities.getMaxAnisotropy();
        texture.colorSpace = SRGBColorSpace; // Is this correct?
    }
    const _onTextSync = (bounds) => {
        //console.log('button cb')
        onTextSync?.(bounds);
    };
    return (_jsx("group", { raycast: raycast, name: `${animationTransformGroupPrefix}${id}`, userData: userData, position: position, rotation: rotation, onPointerUp: onPointerUp || undefined, onPointerDown: onPointerDown || undefined, onPointerMove: onPointerMove || undefined, onDoubleClick: (!disableDoubleClick && onDoubleClick) || undefined, children: _jsx("group", { name: `${animationTransformGroupPrefix}inner_${id}`, children: ((color.length === 1 && !svgUrl) || !!texture) && (_jsx(CurvedEntity, { raycast: raycast, scale: scale, cornerRadius: radius, position: zeroPos, renderOrder: renderOrder, opacity: svgUrl ? 1 : color[0][3], curvature: curvature, side: side ?? DoubleSide, colorRgba: color.length === 1 && !svgUrl ? color[0] : undefined, imageTexture: texture, castShadow: castShadow, receiveShadow: receiveShadow, onTextSync: _onTextSync, isTextScaleLocked: false, border: svgUrl
                    ? undefined
                    : {
                        borderSize: bw,
                        borderRgba: brgba,
                    }, text: svgUrl
                    ? undefined
                    : {
                        value: text,
                        textAlignment,
                        fontSize,
                        fontFamily,
                        fontRgba: fontRgba,
                        side: FrontSide,
                    } }, id)) }) }, 'btn_group'));
};
Button.displayName = 'Button';
export default memo(Button);
